import { closeBrackets, autocompletion } from '@codemirror/autocomplete';
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab
} from '@codemirror/commands';
import { yaml } from '@codemirror/lang-yaml';
import { PostgreSQL, sql, SQLDialect } from '@codemirror/lang-sql';
import { bracketMatching, indentOnInput } from '@codemirror/language';
import { EditorState, type Extension } from '@codemirror/state';
import {
  EditorView,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
  placeholder as placeholderText
} from '@codemirror/view';
import { useEffect, useRef, type FC } from 'react';
import { cn } from '../lib/utils';
import {
  highlightRefs,
  ziggizEditorTheme,
  ziggizHighlighting
} from './code-mirror-theme';

export type CodeEditorLanguage = 'sql' | 'yaml' | 'plain';

/**
 * Databricks (Spark) SQL. `lang-sql` ships no Spark dialect, and a dialect's
 * `keywords` replace the defaults rather than adding to them, so this extends
 * PostgreSQL's list (the closest: window functions, OVER, PARTITION) with
 * Spark's backtick identifiers and extra words.
 */
const SparkSQL = SQLDialect.define({
  ...PostgreSQL.spec,
  identifierQuotes: '`',
  keywords: `${PostgreSQL.spec.keywords ?? ''} qualify rlike regexp explode pivot unpivot distribute sort lateral minutes hours days`
});

interface CodeEditorProps {
  value: string;
  onChange?: (value: string) => void;
  /** Accessible name for the editing surface, e.g. "Custom SQL". */
  label: string;
  language?: CodeEditorLanguage;
  /**
   * Tables and their columns, for SQL completion: `{ previous_step: ['UtcTime', …] }`.
   * `previous_step.` offers its columns; with a single table, so does typing a bare name.
   */
  schema?: Readonly<Record<string, readonly string[]>>;
  readOnly?: boolean;
  placeholder?: string;
  lineNumbers?: boolean;
  autoFocus?: boolean;
  /** ⌘↵ / Ctrl+↵ from inside the editor, e.g. to apply or run. */
  onSubmit?: () => void;
  /** Esc from inside the editor; Tab is taken by indentation, so this is the way out. */
  onEscape?: () => void;
  className?: string;
}

const languageExtension = (
  language: CodeEditorLanguage,
  schema: CodeEditorProps['schema']
): Extension => {
  if (language === 'sql') {
    const tables = schema ? Object.keys(schema) : [];
    return sql({
      dialect: SparkSQL,
      upperCaseKeywords: true,
      schema: schema
        ? Object.fromEntries(
            Object.entries(schema).map(([table, columns]) => [table, [...columns]])
          )
        : undefined,
      // Bare column names only complete against a default table; with one
      // table (`previous_step`), that is the one.
      defaultTable: tables.length === 1 ? tables[0] : undefined
    });
  }
  if (language === 'yaml') return yaml();
  return [];
};

/**
 * The editor for SQL, YAML and plain text, on CodeMirror 6: highlighting
 * while you type, line numbers, undo, bracket matching and schema-aware SQL
 * completion. `CodeBlock` is its read-only sibling for display.
 */
export const CodeEditor: FC<CodeEditorProps> = ({
  value,
  onChange,
  label,
  language = 'plain',
  schema,
  readOnly = false,
  placeholder,
  lineNumbers: showLineNumbers = true,
  autoFocus = false,
  onSubmit,
  onEscape,
  className
}) => {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  // Callbacks change every render; the editor reads them through a ref.
  // `value` rides along so a rebuild (new language, read-only) starts from
  // the current document rather than the first one.
  const handlers = useRef({ onChange, onSubmit, onEscape, value });
  useEffect(() => {
    handlers.current = { onChange, onSubmit, onEscape, value };
  });

  // Compared as a string, so an inline schema object doesn't rebuild the
  // editor (and drop its cursor and undo history) on every render.
  const schemaKey = JSON.stringify(schema ?? null);
  useEffect(() => {
    if (!host.current) return;
    const editor = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: handlers.current.value,
        extensions: [
          showLineNumbers ? [lineNumbers(), highlightActiveLineGutter()] : [],
          highlightActiveLine(),
          history(),
          indentOnInput(),
          bracketMatching(),
          closeBrackets(),
          autocompletion({ icons: false }),
          keymap.of([
            {
              key: 'Mod-Enter',
              run: () => {
                handlers.current.onSubmit?.();
                return handlers.current.onSubmit != null;
              }
            },
            {
              key: 'Escape',
              run: () => {
                handlers.current.onEscape?.();
                return handlers.current.onEscape != null;
              }
            },
            ...defaultKeymap,
            ...historyKeymap,
            indentWithTab
          ]),
          languageExtension(language, JSON.parse(schemaKey) as CodeEditorProps['schema']),
          ziggizEditorTheme,
          ziggizHighlighting,
          highlightRefs,
          placeholder ? placeholderText(placeholder) : [],
          EditorState.readOnly.of(readOnly),
          EditorView.editable.of(!readOnly),
          EditorView.contentAttributes.of({ 'aria-label': label }),
          EditorView.updateListener.of((update) => {
            if (update.docChanged)
              handlers.current.onChange?.(update.state.doc.toString());
          })
        ]
      })
    });
    view.current = editor;
    if (autoFocus) editor.focus();
    return () => {
      editor.destroy();
      view.current = null;
    };
  }, [language, readOnly, showLineNumbers, placeholder, label, schemaKey, autoFocus]);

  // Outside changes (Reset, a fix applied elsewhere) replace the document.
  useEffect(() => {
    const editor = view.current;
    if (!editor || editor.state.doc.toString() === value) return;
    editor.dispatch({ changes: { from: 0, to: editor.state.doc.length, insert: value } });
  }, [value]);

  return <div ref={host} className={cn('min-w-0 text-left', className)} />;
};
